import { createBrowserRouter, type RouteObject } from 'react-router'

import { RequireAuth } from '@/shared/auth/RequireRole'
import { AppShell, type ShellHandle } from '@/shared/shell/AppShell'

import {
  AuthLayout,
  BrowseScreen,
  CheckoutScreen,
  CoursePage,
  EducationAnnouncement,
  EmailChangeCancelScreen,
  EmailChangeConfirmScreen,
  EnquiryInbox,
  EventBrowseScreen,
  EventDetailScreen,
  ListingDetailScreen,
  MyTicketsScreen,
  ListingWizard,
  MyEnquiriesScreen,
  MyListingsScreen,
  AccountScreen,
  NotificationSettings,
  OrdersScreen,
  ProfileEditor,
  PublicProfilePage,
  ForgotPasswordScreen,
  Lazy,
  LoginScreen,
  OnboardingScreen,
  RegisterScreen,
  ResetPasswordScreen,
  VerifyEmailScreen,
} from './lazyRoutes'
import { PORTALS } from './portals'
import { NotFound, PortalRoute, RequireEducation, RequirePayments } from './routeStates'

/**
 * Two trees — §3.4.
 *
 * The portals share the app shell: navigation, role switcher, breadcrumbs. The
 * auth screens deliberately do not. A signed-out visitor has no surfaces to
 * switch between, and rendering the shell around a login form shows them
 * navigation that cannot work.
 *
 * Portal paths end in `/*` so each portal owns its own subtree
 * (`/teach/courses/:courseId`) without this file needing to know about it.
 *
 * Exported separately from `router` so tests can mount the same tree in a memory
 * router — a test that builds its own route list proves nothing about the one
 * that ships.
 */
export const routes: RouteObject[] = [
  {
    element: (
      <Lazy>
        <AuthLayout />
      </Lazy>
    ),
    children: [
      { path: '/login', element: <LoginScreen /> },
      { path: '/register', element: <RegisterScreen /> },
      { path: '/verify-email', element: <VerifyEmailScreen /> },
      { path: '/forgot-password', element: <ForgotPasswordScreen /> },
      { path: '/reset-password', element: <ResetPasswordScreen /> },
      {
        // ⚠️ Path fixed by `email_change.confirm_url()`
        // (`apps/accounts/services/email_change.py`) — every confirm email
        // already sent carries exactly this path.
        path: '/account/email/confirm',
        element: <EmailChangeConfirmScreen />,
      },
      {
        // ⚠️ Path fixed by `email_change.cancel_url()`, same file — the
        // old-address notice email's link.
        path: '/account/email/cancel',
        element: <EmailChangeCancelScreen />,
      },
    ],
  },
  {
    path: '/',
    element: <AppShell />,
    children: [
      ...PORTALS.map((portal) =>
        portal.path === '/'
          ? {
              index: true as const,
              // The homepage paints its own full-width bands (hero, discovery,
              // the wine closing band) — the brand reference's composition,
              // 1 Oct 2026. `AppShell` reads this and drops `<main>`'s
              // `px-gutter pt-6 pb-4` padding for this route only; every band
              // then puts the same 5% gutter on its own content.
              handle: { fullBleed: true } satisfies ShellHandle,
              element: <PortalRoute portal={portal} />,
            }
          : { path: portal.path.replace(/^\//, ''), element: <PortalRoute portal={portal} /> },
      ),
      {
        // Inside the shell, so "Finish this later" leaves onboarding for a page
        // with navigation rather than a dead end.
        path: 'onboarding',
        element: (
          <RequireAuth>
            <Lazy>
              <OnboardingScreen />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        // The public profile. Public, like the course page — a shared link has
        // to open for someone with no account (§3.3).
        path: 'u/:handle',
        element: (
          <Lazy>
            <PublicProfilePage />
          </Lazy>
        ),
      },
      {
        // ⚠️ **The path is fixed by the emails**, not a routing preference:
        // `email.PREFERENCES_PATH` has been putting `/me/notifications` in
        // every footer since Task 1, and the unsubscribe confirmation page
        // links here too. `RequireAuth` and nothing more — preferences belong
        // to an account holder, not to a role.
        path: 'me/notifications',
        element: (
          <RequireAuth>
            <Lazy>
              <NotificationSettings />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        // Roadmap 18.2. `RequireAuth` and nothing more — an account belongs to
        // an account holder, not to a role, exactly like the profile editor
        // and the preference centre beside it.
        path: 'me/account',
        element: (
          <RequireAuth>
            <Lazy>
              <AccountScreen />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: 'me/profile',
        element: (
          <RequireAuth>
            <Lazy>
              <ProfileEditor />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        // The owner's marketplace surfaces — roadmap 14b. All behind
        // `RequireAuth` and nothing more: an owner is any account holder, so
        // there is no role to check. The API enforces the rest — reads are
        // allowed to an unverified owner so they can see their own drafts,
        // writes require a verified account.
        //
        // `new` is listed before `:listingId` so the literal wins the match;
        // otherwise `/me/listings/new` resolves with `listingId === 'new'` and
        // the wizard fetches a listing whose id is the word "new".
        path: 'me/listings',
        element: (
          <RequireAuth>
            <Lazy>
              <MyListingsScreen />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: 'me/enquiries',
        element: (
          <RequireAuth>
            <Lazy>
              <MyEnquiriesScreen />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: 'me/listings/new',
        element: (
          <RequireAuth>
            <Lazy>
              <ListingWizard />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: 'me/listings/:listingId/edit',
        element: (
          <RequireAuth>
            <Lazy>
              <ListingWizard />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: 'me/listings/:listingId/enquiries',
        element: (
          <RequireAuth>
            <Lazy>
              <EnquiryInbox />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        // The education announcement — roadmap 9.5. Public and always mounted,
        // whatever the flag says: it is where the course routes redirect to,
        // and gating it behind the feature it announces would be circular.
        path: 'education',
        element: (
          <Lazy>
            <EducationAnnouncement />
          </Lazy>
        ),
      },
      {
        // An explicit sibling rather than part of the learner splat: the
        // learner portal is an `index` route, and widening it to `/*` would
        // swallow the NotFound below it. Public — a shared course link has to
        // open for someone with no account (§3.3).
        path: 'courses/:id',
        element: (
          <RequireEducation>
            <Lazy>
              <CoursePage />
            </Lazy>
          </RequireEducation>
        ),
      },
      {
        // The catalogue index has no screen of its own yet — the learner portal
        // is still a placeholder — but the path has to resolve, or a link to
        // `/courses` falls through to NotFound instead of the announcement.
        path: 'courses',
        element: (
          <RequireEducation>
            <Lazy>
              <CoursePage />
            </Lazy>
          </RequireEducation>
        ),
      },
      {
        // The public marketplace catalogue — roadmap 14.1. A sibling rather
        // than a portal subtree: the Marketplace deliberately has no portal of
        // its own (§8.4), and browse is public, so it hangs off the shell like
        // the course page does.
        path: 'listings',
        element: (
          <Lazy>
            <BrowseScreen />
          </Lazy>
        ),
      },
      {
        // The public events calendar — roadmap 15.8. A sibling of the
        // marketplace catalogue rather than a portal subtree: events have no
        // portal for attendees, only for organizers, and browse is public.
        path: 'events',
        element: (
          <Lazy>
            <EventBrowseScreen />
          </Lazy>
        ),
      },
      {
        // The public event page. Public like the listing page — this is what a
        // link shared into a group chat opens, and a sign-in wall here is a
        // registration that never happens.
        path: 'events/:id',
        element: (
          <Lazy>
            <EventDetailScreen />
          </Lazy>
        ),
      },
      {
        // The attendee's own tickets. `RequireAuth` and nothing more — an
        // attendee is any account holder, so there is no role to check, and
        // the API filters to the caller's own rows.
        path: 'me/tickets',
        element: (
          <RequireAuth>
            <Lazy>
              <MyTicketsScreen />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        // The checkout screen — roadmap 16.6. `RequireAuth` and nothing more,
        // exactly like the `me/listings` routes above: a buyer is any account
        // holder, not a scoped role, so there is no role to check here either.
        // The API scopes an order to its buyer and answers 404 on any other
        // id (§8.4 rule 2) — the same defence-in-depth shape as every other
        // route in this file, not the actual boundary.
        path: 'checkout/:orderId',
        element: (
          <RequirePayments>
            <RequireAuth>
              <Lazy>
                <CheckoutScreen />
              </Lazy>
            </RequireAuth>
          </RequirePayments>
        ),
      },
      {
        // The buyer's own order history — roadmap 16.10. `RequireAuth` and
        // nothing more, exactly like `checkout/:orderId` above: a buyer is
        // any account holder, not a scoped role. Hidden from platform admins
        // in the navigation (`app/products.tsx`) but the route itself stays
        // mounted, the same shape every other `/me/` surface uses.
        path: 'me/orders',
        element: (
          <RequireAuth>
            <Lazy>
              <OrdersScreen />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        // The public listing page. Public like the course page and for the same
        // reason — a listing shared into a WhatsApp group has to open for
        // someone with no account (§3.3). Not behind `RequireEducation`: the
        // marketplace is Release 1, and education being switched off is
        // unrelated to it.
        path: 'listings/:id',
        element: (
          <Lazy>
            <ListingDetailScreen />
          </Lazy>
        ),
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]

export const router = createBrowserRouter(routes)
